import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Table, Spinner } from 'react-bootstrap';
import Layout from '../components/Layout';
import appConstants from '../constants/appConstants';
import { FaArrowLeft, FaPrint, FaChartBar } from 'react-icons/fa';
import './TestMarksPrint.css';

const getAuthToken = () => localStorage.getItem('authToken');

const TestMarksReport = () => {
  const navigate = useNavigate();
  const { testId } = useParams();
  const [testInfo, setTestInfo] = useState(null);
  const [marks, setMarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const printRef = useRef();

  useEffect(() => {
    fetchTestMarks();
  }, [testId]);

  const fetchTestMarks = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${appConstants.API_ROOT_URL}get-test-marks-v2?test_id=${testId}`,
        {
          headers: { Authorization: `Bearer ${getAuthToken()}` },
        },
      );
      if (res.data.status === 'success') {
        setTestInfo(res.data.test_info);
        setMarks(res.data.students);
      }
    } catch (error) {
      console.error('Failed to fetch test marks', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <Layout>
      <div className="container mt-4">
        {loading ? (
          <div className="text-center mt-5">
            <Spinner animation="border" style={{ color: '#343a40' }} />
            <p className="mt-2 text-muted">Loading test marks...</p>
          </div>
        ) : (
          <>
            {/* Compact Top Bar */}
            <div
              style={{
                background: '#343a40',
                color: '#fff',
                borderRadius: 8,
                padding: '0.6rem 1rem',
                marginBottom: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <FaChartBar />
                  {testInfo?.test_name || 'Test Report'}
                </div>
                <div style={{ fontSize: 13, opacity: 0.9 }}>
                  {testInfo && (
                    <span>
                      {testInfo.exam_date} • {testInfo.subject_name} •{' '}
                      {testInfo.test_type}
                      {testInfo.total_mark &&
                        ` • Total: ${testInfo.total_mark}`}
                    </span>
                  )}
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Button
                  onClick={handlePrint}
                  variant="outline-light"
                  size="sm"
                  style={{
                    borderRadius: 6,
                    padding: '6px 12px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: 13,
                    fontWeight: 500,
                  }}
                >
                  <FaPrint />
                  Print
                </Button>
                <Button
                  onClick={handleBack}
                  variant="outline-light"
                  size="sm"
                  style={{
                    borderRadius: 6,
                    padding: '6px 10px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <FaArrowLeft />
                </Button>
              </div>
            </div>

            {/* Modern Table */}
            <div
              ref={printRef}
              style={{
                backgroundColor: 'white',
                borderRadius: 12,
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                overflowX: 'auto',
              }}
            >
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead
                    style={{
                      background: '#f8f9fa',
                      borderBottom: '3px solid #dee2e6',
                    }}
                  >
                    <tr>
                      <th
                        style={{
                          padding: '12px 20px',
                          fontWeight: '700',
                          color: '#212529',
                          fontSize: '0.95rem',
                          width: '80px',
                          letterSpacing: '0.5px',
                        }}
                      >
                        #
                      </th>
                      <th
                        style={{
                          padding: '12px 20px',
                          fontWeight: '700',
                          color: '#212529',
                          fontSize: '0.95rem',
                          letterSpacing: '0.5px',
                        }}
                      >
                        Student Name
                      </th>
                      <th
                        style={{
                          padding: '12px 20px',
                          fontWeight: '700',
                          color: '#212529',
                          fontSize: '0.95rem',
                          textAlign: 'center',
                          width: '150px',
                          letterSpacing: '0.5px',
                        }}
                      >
                        Mark
                        {testInfo?.total_mark && (
                          <div
                            style={{
                              fontSize: '0.75rem',
                              fontWeight: '500',
                              color: '#6c757d',
                              marginTop: '4px',
                            }}
                          >
                            Out of {testInfo.total_mark}
                          </div>
                        )}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {marks.map((student, index) => (
                      <tr
                        key={student.student_id}
                        style={{ borderBottom: '1px solid #f0f0f0' }}
                      >
                        <td
                          style={{
                            padding: '10px 20px',
                            verticalAlign: 'middle',
                          }}
                        >
                          {index + 1}
                        </td>
                        <td
                          style={{
                            padding: '10px 20px',
                            verticalAlign: 'middle',
                            fontWeight: 500,
                          }}
                        >
                          {student.name}
                        </td>
                        <td
                          style={{
                            padding: '10px 20px',
                            textAlign: 'center',
                            verticalAlign: 'middle',
                            fontWeight: 600,
                          }}
                        >
                          {student.mark !== null &&
                          student.mark !== undefined ? (
                            student.mark
                          ) : (
                            <span
                              style={{ color: '#6c757d', fontStyle: 'italic' }}
                            >
                              Not entered
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>

            {/* Summary Info */}
            <div
              className="mt-3 text-muted text-center"
              style={{ fontSize: '0.9rem' }}
            >
              <strong>Total Students:</strong> {marks.length} •
              <strong> Marks Entered:</strong>{' '}
              {
                marks.filter((s) => s.mark !== null && s.mark !== undefined)
                  .length
              }
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default TestMarksReport;
